import React, { useEffect, useRef, useCallback } from 'react';
import { DatadogLogger } from '../../../../../../shared/helpers/datadog-logger.helper';
import { LogType } from '../../../../../../shared/types/logType';
import { INCODE_BIOMETRIC_FAILURE_STATUSES, INCODE_NON_RETRIABLE_CODES } from '../incodeErrorCodes';

interface IncodeFaceAuthProps2 {
  apiUrl: string;
  identityId: string;
  sessionToken: string;
  sdkVersion: string;
  callBack: (response: any) => void;
  onConnectionError?: () => void;
}

// Extend HTMLScriptElement to include a loaded property
interface ExtendedHTMLScriptElement extends HTMLScriptElement {
  loaded?: boolean;
}

const INCODE_SDK_ORIGIN = 'https://sdk.incode.com';

// Injects preconnect + dns-prefetch hints once so the DNS/TLS handshake to
// sdk.incode.com is warmed before the <script src="..."> request is issued.
function ensureIncodePreconnect(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('incode-sdk-preconnect')) return;

  const preconnect = document.createElement('link');
  preconnect.id = 'incode-sdk-preconnect';
  preconnect.rel = 'preconnect';
  preconnect.href = INCODE_SDK_ORIGIN;
  preconnect.crossOrigin = 'anonymous';
  document.head.appendChild(preconnect);

  const dnsPrefetch = document.createElement('link');
  dnsPrefetch.rel = 'dns-prefetch';
  dnsPrefetch.href = INCODE_SDK_ORIGIN;
  document.head.appendChild(dnsPrefetch);
}

const IncodeAuth = ({ apiUrl, identityId, sessionToken, sdkVersion, callBack, onConnectionError }: IncodeFaceAuthProps2) => {
  // A ref to hold the Incode SDK instance.
  const incodeRef = useRef<any>(null);
  // A ref for the container element where the SDK will be mounted.
  const incodeContainerRef = useRef<HTMLElement | null>(null);
  // Reference to track script element
  const scriptRef = useRef<ExtendedHTMLScriptElement | null>(null);
  // Flag to prevent re-initialization
  const isInitializedRef = useRef<boolean>(false);
  
  // Memoize the callbacks to prevent unnecessary effect reruns
  const handleSuccess = useCallback((incodeResponse: any) => {
    // renderAuthFace onSuccess returns { overallStatus, candidate, imageBase64, ... }
    callBack(incodeResponse);
  }, [callBack]);

  const handleError = useCallback((error: any) => {
    const errorStatus  = error?.status  ?? null;
    const errorCode    = error?.code    ?? null;
    const errorMessage = error?.message ?? 'Unknown error';

    try {
      DatadogLogger.Log('INCODE_SDK_ERROR', {
        event:             'INCODE_SDK_ERROR',
        errorStatus,
        errorCode,
        errorMessage,
        livenessSessionId: identityId,
        environment:       process.env.NODE_ENV,
        timestamp:         new Date().toISOString(),
      }, LogType.ERROR);
    } catch {
      // Intentionally silent — Datadog must never break the liveness flow
    }

    const isBiometricFailure =
      errorStatus !== null &&
      INCODE_BIOMETRIC_FAILURE_STATUSES.includes(Number(errorStatus));

    const isNonRetriableCode =
      errorCode !== null &&
      INCODE_NON_RETRIABLE_CODES.includes(String(errorCode));

    if (!isBiometricFailure && !isNonRetriableCode && onConnectionError) {
      onConnectionError();
    } else {
      callBack({
        error: true,
        errorStatus,
        errorCode,
        errorMessage,
      });
    }
  }, [callBack, identityId, onConnectionError]);
  
  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      return;
    }

    ensureIncodePreconnect();

    const mountStart = performance.now();
    let scriptLoadStart = mountStart;
    let scriptLoadDurationMs = 0;

    const logTiming = (extra: Record<string, unknown>) => {
      try {
        DatadogLogger.Log('INCODE_SDK_TIMING', {
          event:             'INCODE_SDK_TIMING',
          sdkVersion,
          livenessSessionId: identityId,
          environment:       process.env.NODE_ENV,
          timestamp:         new Date().toISOString(),
          ...extra,
        }, LogType.INFO);
      } catch {
        // Intentionally silent — Datadog must never break the liveness flow
      }
    };

    const setupIncode = async () => {
      if (window.OnBoarding && incodeContainerRef.current && !isInitializedRef.current) {
        isInitializedRef.current = true;

        const initStart = performance.now();

        // 1. Create the Web SDK instance
        const incode = window.OnBoarding.create({
          apiURL: apiUrl,
        });
        incodeRef.current = incode;

        // 2. initialize() is mandatory from SDK v1.83.0+
        await incode.initialize();

        const sdkInitDurationMs = performance.now() - initStart;

        // 3. Render the face authentication flow using renderAuthFace (available from v1.84.0+)
        incode.renderAuthFace(incodeContainerRef.current, {
          session: {
            token: sessionToken,
          },
          authHint: identityId,
          onSuccess: handleSuccess,
          onError: handleError,
        });

        logTiming({
          scriptLoadDurationMs: Math.round(scriptLoadDurationMs),
          sdkInitDurationMs:    Math.round(sdkInitDurationMs),
          sdkReadyDurationMs:   Math.round(performance.now() - mountStart),
        });
      }
    };

    // Script ID for identifying our script
    const scriptId = `incode-sdk-${sdkVersion}`;

    // Check if the script is already loaded
    let existingScript = document.getElementById(scriptId) as ExtendedHTMLScriptElement;

    if (existingScript) {
      scriptRef.current = existingScript;

      if (existingScript.loaded) {
        // Script was already parsed by a previous mount — no network cost.
        scriptLoadDurationMs = 0;
        logTiming({ fromCache: true, scriptLoadDurationMs: 0 });
        setupIncode();
        return;
      } else {
        const onLoad = () => {
          scriptLoadDurationMs = performance.now() - scriptLoadStart;
          setupIncode();
        };
        existingScript.addEventListener('load', onLoad);
        return () => {
          existingScript.removeEventListener('load', onLoad);
          isInitializedRef.current = false;
        };
      }
    }

    // Validate SDK version format to prevent script injection (only allow digits and dots)
    if (!/^[\d.]+$/.test(sdkVersion)) {
      console.error('Invalid SDK version format:', sdkVersion);
      return;
    }

    // If not, create and append the script to the document
    const script = document.createElement('script') as ExtendedHTMLScriptElement;
    script.id = scriptId;
    script.src = `${INCODE_SDK_ORIGIN}/sdk/onBoarding-${sdkVersion}.js`;
    script.async = true;
    script.defer = true;
    // Hint to the browser this script is critical for the current view.
    (script as HTMLScriptElement & { fetchPriority?: string }).fetchPriority = 'high';
    scriptLoadStart = performance.now();
    script.onload = () => {
      script.loaded = true;
      scriptLoadDurationMs = performance.now() - scriptLoadStart;
      setupIncode();
    };

    scriptRef.current = script;
    document.body.appendChild(script);

    // Cleanup function
    return () => {
      isInitializedRef.current = false;
      
      // Clean up any SDK resources if needed
      if (incodeRef.current && typeof incodeRef.current.destroy === 'function') {
        incodeRef.current.destroy();
      }
      
      // We don't remove the script to avoid issues with other components that might use it
      // Instead we just clean up our event listeners
      if (scriptRef.current) {
        scriptRef.current.removeEventListener('load', setupIncode);
      }
    };
  }, [apiUrl, identityId, sessionToken, sdkVersion, handleSuccess, handleError]); // Include all dependencies

  return <section ref={incodeContainerRef} id="incode-container" />;
};

export default IncodeAuth;